// Giving people rides: seats per vehicle, and the fare (plus a lucky tip) when you drop someone off.
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { TUNABLES } from "@degen/shared/config/tunables";
import { money } from "@degen/shared/money";
import { CARS, isBike, type VehicleBody } from "./cars";

/** Passenger seats (not counting you). The Danfo is a bus. */
export function seatsFor(body: VehicleBody): number {
  if (isBike(body)) return body === "superbike" || body === "sportbike" ? 1 : body === "okada" ? 2 : 1;
  if (body === "van") return 6;
  if (body === "suv" || body === "pickup") return 4;
  if (body === "coupe" || body === "super") return 1;
  return 3;
}

export interface RidesState {
  day: number;
  paidToday: number;
  total: number;
  earned: number;
}

export type RideResult = { ok: true; data: { fare: number; tip: number; jackpot: number; followers: number; paid: number } } | { ok: false; error: string };

export function giveRide(state: GameState, rng: Rng, accountId: string, meters: number, modelId: string): RideResult {
  const R = TUNABLES.rides;
  // Real players ride too: "p:<handle>" (the island pays the fare, not them).
  const real = /^p:[a-zA-Z0-9_]{2,24}$/.test(accountId) ? { handle: accountId.slice(2) } : null;
  const acc = real ?? state.social.accounts.find((a) => a.id === accountId);
  if (!acc) return { ok: false, error: "No such person." };
  const m = CARS.find((c) => c.id === modelId);
  if (!m) return { ok: false, error: "No such vehicle." };
  if (!Number.isFinite(meters) || meters < 0) return { ok: false, error: "Bad distance." };
  const day = dayOf(state.tick);
  const rides = (state.cars.rides ??= { day, paidToday: 0, total: 0, earned: 0 });
  if (rides.day !== day) {
    rides.day = day;
    rides.paidToday = 0;
  }
  rides.total++;
  const dist = Math.min(R.maxMeters, meters);
  const p = state.player;
  if (dist < R.minMeters || rides.paidToday >= R.maxPaidPerDay) {
    pushLog(state, "ride", `🚗 Dropped @${acc.handle} off. ${dist < R.minMeters ? "Short hop, no fare." : "They said thanks 🙏 (you've done enough paid rides today)."}`);
    return { ok: true, data: { fare: 0, tip: 0, jackpot: 0, followers: 0, paid: 0 } };
  }
  rides.paidToday++;
  const mul = (R.tierMul[m.tier] ?? 1) * (isBike(m.body) ? R.bikeMul : m.body === "van" ? R.vanMul : 1);
  const fare = Math.min(R.fareCap, (R.base + (dist / 1000) * R.perKm) * mul);
  let tip = rng.chance(R.tipChance) ? fare * rng.range(R.tipRange[0], R.tipRange[1]) : 0;
  const jackpot = rng.chance(R.jackpotChance);
  if (jackpot) tip += fare * R.jackpotMul;
  let followers = 0;
  if ((m.tier === "luxury" || m.tier === "exotic" || m.tier === "super") && rng.chance(R.flexChance)) {
    followers = Math.round(rng.range(R.flexFollowers[0], R.flexFollowers[1]));
    p.followers += followers;
  }
  const total = fare + tip;
  p.cash += total;
  rides.earned += total;
  const extra = jackpot ? ` 🐳 A WHALE TIP: +${money(tip)}!` : tip > 0 ? ` +${money(tip)} tip 🎉` : "";
  const flex = followers ? ` They posted the ride: +${followers} followers.` : "";
  pushLog(state, "ride", `🚗 Dropped @${acc.handle} off: ${money(fare)} fare.${extra}${flex}`);
  return { ok: true, data: { fare, tip, jackpot: jackpot ? 1 : 0, followers, paid: 1 } };
}
